import { useRef, useState } from 'react'

import { cn } from '@/lib/utils'
import { gsap, registerGsap, prefersReducedMotion } from '@/lib/gsap'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'

interface ProductGalleryProps {
    images: string[]
    name: string
}

export function ProductGallery({ images, name }: ProductGalleryProps) {
    const [active, setActive] = useState(0)
    const mainRef = useRef<HTMLImageElement>(null)
    const mounted = useRef(false)

    // Crossfade the main image when a thumbnail is chosen.
    useIsomorphicLayoutEffect(() => {
        const el = mainRef.current
        if (!el) return
        registerGsap()
        if (!mounted.current) {
            mounted.current = true
            return
        }
        if (prefersReducedMotion()) return
        gsap.fromTo(
            el,
            { opacity: 0, scale: 1.04 },
            { opacity: 1, scale: 1, duration: 0.5, ease: 'power2.out' }
        )
    }, [active])

    const src = images[active] ?? images[0]

    return (
        <div className='product-gallery'>
            <div className='product-gallery-main'>
                <img
                    ref={mainRef}
                    src={src}
                    alt={name}
                    className='product-gallery-img'
                />
            </div>
            {images.length > 1 ? (
                <div className='product-gallery-thumbs'>
                    {images.map((image, index) => (
                        <button
                            key={image + index}
                            type='button'
                            aria-label={`View image ${index + 1}`}
                            className={cn(
                                'product-gallery-thumb',
                                index === active && 'is-active'
                            )}
                            onClick={() => setActive(index)}
                            style={{ cursor: 'none' }}
                        >
                            <img src={image} alt='' />
                        </button>
                    ))}
                </div>
            ) : null}
        </div>
    )
}
